import type { ScrollState } from 'hooks/useScrollArea';
import {
  ComponentProps,
  ForwardRefRenderFunction,
  forwardRef,
  MutableRefObject,
  useMemo,
} from 'react';
import { type Availability, getConflicts } from 'utils/booking';
import { isPastDate } from 'utils/date';
import type { ScrollAreaHandle } from '../scrollArea';
import LoadingOverlay from '../shared/loadingOverlay';
import Header from './header';
import SlotSelection from './slotSelection';
import Space from './space';

interface SpaceType {
  id: number;
  openingTimes?: { [key: string]: string };
}

// A booking item stored on the calendar's per-space value: the drawn times
// plus, for existing bookings, ids and billing state.
export interface CalendarValueItem {
  startTime: string;
  endTime: string;
  id?: number;
  state?: string;
  statementState?: string;
}

// A raw booking item handed to the row, still carrying its space so it can be
// grouped by space key.
export interface RowBookingItem extends CalendarValueItem {
  space?: { id?: number };
}

interface Props {
  name: string;
  date: string;
  venue: ComponentProps<typeof SlotSelection>['venue'];
  spaces: SpaceType[];
  // Keyed by space key (`s<id>`), like `touched` and `value` below.
  availability?: Record<string, Availability>;
  disabled?: boolean;
  scrollRef: MutableRefObject<ScrollAreaHandle>;
  scrollState: ScrollState;
  times: string[];
  startTime?: string;
  endTime?: string;
  clinical?: boolean;
  slots?: ComponentProps<typeof SlotSelection>['slots'];
  bookingItems?: RowBookingItem[];
  onChangeSpace?: (date: string, space: SpaceType) => void;
  onRemove?: () => void;
  onRemoveSpace?: (date: string, spaceId: number) => void;
  spaceDetailsComponent?: ComponentProps<typeof Space>['detailsComponent'];
  headerComponent?: typeof Header;
  readOnly?: boolean;
  loading?: boolean;
  confirmed?: boolean;
  touched?: Record<string, boolean>;
  setTouched?: (touched: object) => void;
  value?: Record<string, CalendarValueItem[]>;
  setValue?: (value: object) => void;
}

const wrapSetter =
  (setter: (value: object) => void, value: object = {}, key: string) =>
  (innerValue: unknown) =>
    setter({ ...value, [key]: innerValue });

const Row: ForwardRefRenderFunction<HTMLDivElement, Props> = (
  {
    name,
    date,
    times,
    venue,
    clinical,
    scrollRef,
    scrollState,
    startTime,
    endTime,
    slots = [],
    bookingItems = [],
    spaces,
    availability = {},
    disabled,
    onChangeSpace: changeSpace,
    onRemove,
    onRemoveSpace: removeSpace,
    spaceDetailsComponent,
    headerComponent: HeaderComponent = Header,
    loading = false,
    touched = {},
    setTouched = () => null,
    value = {},
    setValue = () => null,
  },
  forwardedRef
) => {
  const clashCount = useMemo(
    () =>
      spaces.flatMap(({ id }) => getConflicts(availability[`s${id}`] || {}))
        .length,
    [availability, spaces]
  );

  const bookingItemsBySpace = useMemo(
    () =>
      spaces
        .map(({ id }) => ({
          [`s${id}`]: bookingItems.filter(
            ({ space: { id: spaceId } = {} }) => spaceId === id
          ),
        }))
        .reduce<Record<string, RowBookingItem[]>>(
          (prev, curr) => ({ ...prev, ...curr }),
          {}
        ),
    [spaces, bookingItems]
  );

  const renderField = (space: SpaceType, key: string) => {
    const slotSelectionProps = {
      name: `${name}.${key}`,
      date,
      clinical,
      venue,
      space,
      startTime: startTime as string,
      endTime: endTime as string,
      times,
      slots,
      bookingItems: bookingItemsBySpace[key],
      disabled,
      scrollRef,
      availability: availability[key],
      touched: touched[key],
      setTouched: wrapSetter(setTouched, touched, key),
      value: value[key],
      onChange: wrapSetter(setValue, value, key),
    };

    return loading ? null : <SlotSelection {...slotSelectionProps} />;
  };

  const isPastBooking = useMemo(() => isPastDate(date), [date]);

  const isBilled = (space: SpaceType) => {
    if (value[`s${space.id}`]) {
      const bookingItems = value[`s${space.id}`] || [];

      return bookingItems.some(
        (bookingItem: { statementState?: unknown }) =>
          !!bookingItem.statementState
      );
    }

    return false;
  };

  return (
    <div className="calendar__row" ref={forwardedRef}>
      <HeaderComponent date={date} clashCount={clashCount} onClick={onRemove} />

      {spaces.map(space => {
        const key = `s${space.id}`;

        return (
          <LoadingOverlay
            key={space.id}
            active={isPastBooking || isBilled(space)}
            spinner={<></>}
          >
            <Space
              space={space as unknown as ComponentProps<typeof Space>['space']}
              times={times}
              scrollRef={scrollRef}
              scrollState={scrollState}
              clinical={clinical}
              setValue={wrapSetter(setValue, value, key)}
              setTouched={wrapSetter(setTouched, touched, key)}
              onChange={
                changeSpace ? () => changeSpace(date, space) : undefined
              }
              onRemove={
                removeSpace ? () => removeSpace(date, space.id) : undefined
              }
              detailsComponent={spaceDetailsComponent}
            >
              {renderField(space, key)}
            </Space>
          </LoadingOverlay>
        );
      })}
    </div>
  );
};

export default forwardRef(Row);
